/**
 * BOMs API client (Phase 3c Step 7).
 * Uses relative `/api` — Vite proxy injects X-Api-Token server-side.
 */

import type {
  Bom,
  BomCreateInput,
  BomDetail,
  BomHardwareLine,
  BomHeaderPatchInput,
  BomPackagingLine,
  BomPackagingOption,
  BomStatus,
  BomWoodLine,
} from './types'

export type BomsApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class BomsApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'BomsApiClientError'
    this.code = code
    this.status = status
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as BomsApiError
  throw new BomsApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

export async function listBomsApi(filter?: {
  status?: BomStatus
  product_id?: string
  q?: string
}): Promise<Bom[]> {
  const params = new URLSearchParams()
  if (filter?.status) params.set('status', filter.status)
  if (filter?.product_id) params.set('product_id', filter.product_id)
  if (filter?.q) params.set('q', filter.q)
  const qs = params.toString()
  const res = await fetch(`/api/boms${qs ? `?${qs}` : ''}`)
  const body = await parseJson<{ ok: true; boms: Bom[] } | BomsApiError>(res)
  throwIfApiError(res, body, 'Failed to list BOMs')
  return (body as { boms: Bom[] }).boms
}

export async function getBomApi(id: string): Promise<BomDetail | null> {
  const res = await fetch(`/api/boms/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<{ ok: true; bom: BomDetail } | BomsApiError>(res)
  throwIfApiError(res, body, 'Failed to get BOM')
  return (body as { bom: BomDetail }).bom
}

export async function createBomApi(input: BomCreateInput): Promise<BomDetail> {
  const res = await fetch('/api/boms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<{ ok: true; bom: BomDetail } | BomsApiError>(res)
  throwIfApiError(res, body, 'Tạo BOM thất bại')
  return (body as { bom: BomDetail }).bom
}

export async function patchBomHeaderApi(
  id: string,
  patch: BomHeaderPatchInput,
): Promise<Bom> {
  const res = await fetch(`/api/boms/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  const body = await parseJson<{ ok: true; bom: Bom } | BomsApiError>(res)
  throwIfApiError(res, body, 'Cập nhật BOM thất bại')
  return (body as { bom: Bom }).bom
}

export async function activateBomApi(id: string): Promise<BomDetail> {
  const res = await fetch(`/api/boms/${encodeURIComponent(id)}/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
  const body = await parseJson<{ ok: true; bom: BomDetail } | BomsApiError>(res)
  throwIfApiError(res, body, 'Activate BOM thất bại')
  return (body as { bom: BomDetail }).bom
}

export type SaveActiveBomInput = {
  base_version_seq: number
  edit_note?: string | null
  wood_lines?: Array<{
    id?: string
    sort_order?: number
    part_name: string
    part_group?: string | null
    material_id?: string | null
    wood_type_raw?: string | null
    dims_finished_t?: number | null
    dims_finished_w?: number | null
    dims_finished_l?: number | null
    dims_rough_t?: number | null
    dims_rough_w?: number | null
    dims_rough_l?: number | null
    tenon?: string | null
    qty_per: number
    veneer_spec?: string | null
    notes?: string | null
  }>
  hardware_lines?: Array<{
    id?: string
    sort_order?: number
    name_raw: string
    spec_raw?: string | null
    material_id?: string | null
    qty_per: number
    uom_hint?: string | null
    color_hint?: string | null
    notes?: string | null
  }>
}

export async function saveActiveBomApi(
  id: string,
  input: SaveActiveBomInput,
): Promise<BomDetail> {
  const res = await fetch(`/api/boms/${encodeURIComponent(id)}/save-active`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<{ ok: true; bom: BomDetail } | BomsApiError>(res)
  throwIfApiError(res, body, 'Lưu BOM thất bại')
  return (body as { bom: BomDetail }).bom
}

export async function getBomFamilyVersionsApi(
  familyId: string,
): Promise<Bom[]> {
  const res = await fetch(
    `/api/boms/families/${encodeURIComponent(familyId)}/versions`,
  )
  const body = await parseJson<
    { ok: true; family_id: string; versions: Bom[] } | BomsApiError
  >(res)
  throwIfApiError(res, body, 'Lấy danh sách phiên bản BOM thất bại')
  return (body as { versions: Bom[] }).versions
}

export async function createWoodLineApi(
  bomId: string,
  input: Record<string, unknown>,
): Promise<BomWoodLine> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/wood-lines`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<
    { ok: true; woodLine: BomWoodLine } | BomsApiError
  >(res)
  throwIfApiError(res, body, 'Thêm dòng gỗ thất bại')
  return (body as { woodLine: BomWoodLine }).woodLine
}

export async function updateWoodLineApi(
  bomId: string,
  lineId: string,
  patch: Record<string, unknown>,
): Promise<BomWoodLine> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/wood-lines/${encodeURIComponent(lineId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  )
  const body = await parseJson<
    { ok: true; woodLine: BomWoodLine } | BomsApiError
  >(res)
  throwIfApiError(res, body, 'Sửa dòng gỗ thất bại')
  return (body as { woodLine: BomWoodLine }).woodLine
}

export async function deleteWoodLineApi(
  bomId: string,
  lineId: string,
): Promise<void> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/wood-lines/${encodeURIComponent(lineId)}`,
    { method: 'DELETE' },
  )
  const body = await parseJson<{ ok: true } | BomsApiError>(res)
  throwIfApiError(res, body, 'Xóa dòng gỗ thất bại')
}

export async function createPackagingOptionApi(
  bomId: string,
  input: Record<string, unknown>,
): Promise<BomPackagingOption> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/packaging-options`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<
    { ok: true; packagingOption: BomPackagingOption } | BomsApiError
  >(res)
  throwIfApiError(res, body, 'Thêm phương án đóng gói thất bại')
  return (body as { packagingOption: BomPackagingOption }).packagingOption
}

export async function updatePackagingOptionApi(
  bomId: string,
  optionId: string,
  patch: Record<string, unknown>,
): Promise<BomPackagingOption> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/packaging-options/${encodeURIComponent(optionId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  )
  const body = await parseJson<
    { ok: true; packagingOption: BomPackagingOption } | BomsApiError
  >(res)
  throwIfApiError(res, body, 'Sửa phương án đóng gói thất bại')
  return (body as { packagingOption: BomPackagingOption }).packagingOption
}

export async function deletePackagingOptionApi(
  bomId: string,
  optionId: string,
): Promise<void> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/packaging-options/${encodeURIComponent(optionId)}`,
    { method: 'DELETE' },
  )
  const body = await parseJson<{ ok: true } | BomsApiError>(res)
  throwIfApiError(res, body, 'Xóa phương án đóng gói thất bại')
}

export async function createHardwareLineApi(
  bomId: string,
  input: Record<string, unknown>,
): Promise<BomHardwareLine> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/hardware-lines`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<
    { ok: true; hardwareLine: BomHardwareLine } | BomsApiError
  >(res)
  throwIfApiError(res, body, 'Thêm dòng ngũ kim thất bại')
  return (body as { hardwareLine: BomHardwareLine }).hardwareLine
}

export async function updateHardwareLineApi(
  bomId: string,
  lineId: string,
  patch: Record<string, unknown>,
): Promise<BomHardwareLine> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/hardware-lines/${encodeURIComponent(lineId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  )
  const body = await parseJson<
    { ok: true; hardwareLine: BomHardwareLine } | BomsApiError
  >(res)
  throwIfApiError(res, body, 'Sửa dòng ngũ kim thất bại')
  return (body as { hardwareLine: BomHardwareLine }).hardwareLine
}

export async function deleteHardwareLineApi(
  bomId: string,
  lineId: string,
): Promise<void> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/hardware-lines/${encodeURIComponent(lineId)}`,
    { method: 'DELETE' },
  )
  const body = await parseJson<{ ok: true } | BomsApiError>(res)
  throwIfApiError(res, body, 'Xóa dòng ngũ kim thất bại')
}

export async function createPackagingLineApi(
  bomId: string,
  input: Record<string, unknown>,
): Promise<BomPackagingLine> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/packaging-lines`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<
    { ok: true; packagingLine: BomPackagingLine } | BomsApiError
  >(res)
  throwIfApiError(res, body, 'Thêm dòng đóng gói thất bại')
  return (body as { packagingLine: BomPackagingLine }).packagingLine
}

export async function updatePackagingLineApi(
  bomId: string,
  lineId: string,
  patch: Record<string, unknown>,
): Promise<BomPackagingLine> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/packaging-lines/${encodeURIComponent(lineId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  )
  const body = await parseJson<
    { ok: true; packagingLine: BomPackagingLine } | BomsApiError
  >(res)
  throwIfApiError(res, body, 'Sửa dòng đóng gói thất bại')
  return (body as { packagingLine: BomPackagingLine }).packagingLine
}

export async function deletePackagingLineApi(
  bomId: string,
  lineId: string,
): Promise<void> {
  const res = await fetch(
    `/api/boms/${encodeURIComponent(bomId)}/packaging-lines/${encodeURIComponent(lineId)}`,
    { method: 'DELETE' },
  )
  const body = await parseJson<{ ok: true } | BomsApiError>(res)
  throwIfApiError(res, body, 'Xóa dòng đóng gói thất bại')
}

export type CloneBomInput = {
  name?: string
  target_product_id?: string | null
  target_assembly_type?: 'assembled' | 'kd'
}

export async function cloneBomApi(
  id: string,
  input: CloneBomInput = {},
): Promise<BomDetail> {
  const res = await fetch(`/api/boms/${encodeURIComponent(id)}/clone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<{ ok: true; bom: BomDetail } | BomsApiError>(res)
  throwIfApiError(res, body, 'Nhân bản BOM thất bại')
  return (body as { bom: BomDetail }).bom
}

export type BomCompareLineDiff = {
  type: 'wood' | 'hardware'
  name: string
  base: Record<string, unknown>
  target: Record<string, unknown>
  diffs: Record<string, { base: unknown; target: unknown }>
}

export type BomCompareResult = {
  ok: true
  base_id: string
  target_id: string
  base_bom_id: string
  target_bom_id: string
  added_lines: Array<Record<string, unknown>>
  removed_lines: Array<Record<string, unknown>>
  changed_lines: BomCompareLineDiff[]
  wood_lines: {
    added: BomWoodLine[]
    removed: BomWoodLine[]
    changed: Array<{
      name: string
      base: BomWoodLine
      target: BomWoodLine
      diffs: Record<string, { base: unknown; target: unknown }>
    }>
  }
  hardware_lines: {
    added: BomHardwareLine[]
    removed: BomHardwareLine[]
    changed: Array<{
      name: string
      base: BomHardwareLine
      target: BomHardwareLine
      diffs: Record<string, { base: unknown; target: unknown }>
    }>
  }
}

export async function compareBomsApi(
  baseId: string,
  targetId: string,
): Promise<BomCompareResult> {
  const params = new URLSearchParams({
    base_id: baseId,
    target_id: targetId,
  })
  const res = await fetch(`/api/boms/compare?${params.toString()}`)
  const body = await parseJson<BomCompareResult | BomsApiError>(res)
  throwIfApiError(res, body, 'So sánh BOM thất bại')
  return body as BomCompareResult
}

